import { useAuth } from '~/store';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface PendingChange {
  id: string;
  toolName: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  entityType: string;
  entityId?: string;
  label: string;
  before: Record<string, any> | null;
  after: Record<string, any>;
  input: Record<string, any>;
  context?: Record<string, any>;
  confirmed?: boolean;
}

export interface StreamResult {
  text: string;
  sessionId?: string;
  pendingChanges?: PendingChange[];
}

const API_URL = import.meta.env.VITE_API_URL;

function getHeaders() {
  const tokens = useAuth.getState().tokens;
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${tokens?.accessToken}`,
  };
}

export const aiChatApi = {
  streamChat: async function* (
    messages: ChatMessage[],
    sessionId?: string,
  ): AsyncGenerator<StreamResult> {
    const response = await fetch(`${API_URL}/ai-chat/stream`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ messages, sessionId }),
    });

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith('data: ')) continue;
        const data = trimmed.slice(6);
        if (data === '[DONE]') return;

        try {
          const parsed = JSON.parse(data);
          if (parsed.error) throw new Error(parsed.error);
          if (parsed.text) {
            // Check for special prefixes
            if (parsed.text.startsWith('__SESSION__')) {
              yield { text: '', sessionId: parsed.text.replace('__SESSION__', '') };
            } else if (parsed.text.startsWith('__PENDING__')) {
              const pendingChanges = JSON.parse(parsed.text.replace('__PENDING__', ''));
              yield { text: '', pendingChanges };
            } else {
              yield { text: parsed.text };
            }
          }
        } catch (e) {
          if (e instanceof SyntaxError) continue;
          throw e;
        }
      }
    }
  },

  confirmChanges: async (sessionId: string, pendingChanges: PendingChange[]) => {
    const response = await fetch(`${API_URL}/ai-chat/confirm`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({ sessionId, pendingChanges }),
    });

    if (!response.ok) {
      throw new Error(`Confirm changes failed: ${response.status}`);
    }

    return response.json();
  },

  getSessions: async () => {
    const response = await fetch(`${API_URL}/ai-chat/sessions`, {
      headers: getHeaders(),
    });
    return response.json();
  },

  getSession: async (id: string) => {
    const response = await fetch(`${API_URL}/ai-chat/sessions/${id}`, {
      headers: getHeaders(),
    });
    return response.json();
  },
};
